import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  #dismissed = false;

  /** @type {Set<string>} */
  #cartVariantIds = new Set();

  /** @type {{ id: number, x: number, y: number } | null} */
  #swipeStart = null;

  #suppressClickUntil = 0;

  #selectionVersion = 0;

  #cartVersion = 0;

  #appliedCartVersion = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    this.#cartVariantIds = new Set((this.dataset.cartVariantIds || '').split(',').filter(Boolean));
    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.addEventListener('pointerdown', this.#handleSwipeStart, { signal });
    this.addEventListener('pointerup', this.#handleSwipeEnd, { signal });
    this.addEventListener('pointercancel', this.#cancelSwipe, { signal });
    this.addEventListener('click', this.#guardSwipeClick, { capture: true, signal });
    this.addEventListener('keydown', this.#handleDismissKey, { signal });

    this.#getInitialQuantity();
    this.#syncSizeChoice();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    this.#swipeStart = null;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(() => this.#syncVisibility());

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      () => this.#syncVisibility(),
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  dismissStickyBar = () => {
    this.#dismissed = true;
    this.#hideStickyBar();
  };

  /** @param {PointerEvent} event */
  #handleSwipeStart = (event) => {
    this.#swipeStart = null;
    if (event.pointerType !== 'touch' || !event.isPrimary || !this.#isStuck) return;
    if (!(event.target instanceof Node) || !this.refs.stickyBar.contains(event.target)) return;
    this.#swipeStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
  };

  /** @param {PointerEvent} event */
  #handleSwipeEnd = (event) => {
    const start = this.#swipeStart;
    this.#swipeStart = null;
    if (!start || start.id !== event.pointerId) return;
    const distanceX = Math.abs(event.clientX - start.x);
    const distanceY = Math.abs(event.clientY - start.y);
    if (distanceX < 48 || distanceX < distanceY * 1.4) return;
    this.#suppressClickUntil = Date.now() + 500;
    this.dismissStickyBar();
  };

  #cancelSwipe = () => {
    this.#swipeStart = null;
  };

  /** @param {MouseEvent} event */
  #guardSwipeClick = (event) => {
    if (Date.now() >= this.#suppressClickUntil) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  /** @param {KeyboardEvent} event */
  #handleDismissKey = (event) => {
    if (event.key !== 'Escape' || !this.#isStuck) return;
    event.preventDefault();
    this.dismissStickyBar();
  };

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (Date.now() < this.#suppressClickUntil) return;
    const form = this.#getProductForm();
    if (form?.needsSizeChoice) {
      form.promptForSize();
      return;
    }
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    this.#syncSizeChoice();
    const selectionVersion = ++this.#selectionVersion;
    const { signal } = this.#abortController;

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (signal.aborted || selectionVersion !== this.#selectionVersion) return;
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
        this.#syncSizeChoice();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    const targetButton = this.#targetAddToCartButton;
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (targetButton && event.target === this.#getProductForm()) {
        targetButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      const { signal } = this.#abortController;
      const cartVersion = ++this.#cartVersion;
      event.promise.then(({ detail }) => {
        if (signal.aborted || detail?.didError || !Array.isArray(detail?.items)) return;
        if (cartVersion < this.#appliedCartVersion) return;
        this.#appliedCartVersion = cartVersion;
        this.#cartVariantIds = new Set(detail.items.filter((item) => item.quantity > 0).map((item) => String(item.variant_id)));
        this.dataset.cartVariantIds = [...this.#cartVariantIds].join(',');
        this.#syncVisibility();
      }).catch(() => {}).finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    if (this.#dismissed || this.#isCurrentVariantInCart()) return;
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    stickyBar.inert = false;
    stickyBar.setAttribute('aria-hidden', 'false');
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    if (stickyBar.contains(document.activeElement)) {
      const main = this.closest('main');
      if (main) {
        const previousTabIndex = main.getAttribute('tabindex');
        main.tabIndex = -1;
        main.focus({ preventScroll: true });
        if (previousTabIndex === null) main.removeAttribute('tabindex');
        else main.setAttribute('tabindex', previousTabIndex);
      }
    }
    stickyBar.inert = true;
    stickyBar.setAttribute('aria-hidden', 'true');
  }

  #syncVisibility() {
    const buyButtons = this.#getProductForm()?.closest('.buy-buttons-block');
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!buyButtons || !footer) return;
    const buyRect = buyButtons.getBoundingClientRect();
    const footerRect = footer.getBoundingClientRect();
    const footerVisible = footerRect.top < window.innerHeight && footerRect.bottom > -200;
    if (buyRect.bottom < 0 && !footerVisible && !this.#isChatActive() && !this.#dismissed &&
      this.dataset.variantAvailable === 'true' &&
      !this.#isCurrentVariantInCart()) {
      this.#showStickyBar();
    } else {
      this.#hideStickyBar();
    }
  }

  #isCurrentVariantInCart() {
    return !this.#getProductForm()?.needsSizeChoice &&
      this.#cartVariantIds.has(this.dataset.currentVariantId || '');
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  #syncSizeChoice() {
    const form = this.#getProductForm();
    const needed = form?.needsSizeChoice ?? this.querySelector('[data-hd-needs-size]') !== null;
    this.refs.addToCartButton.toggleAttribute('data-hd-needs-size', needed && !this.refs.addToCartButton.disabled);
    const variantTitle = this.querySelector('.sticky-add-to-cart__variant');
    const section = this.closest('.shopify-section');
    const picker = section?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (variantTitle && picker) {
      const values = Array.from(picker.querySelectorAll('input:checked, select option:checked[data-option-value-id]')).map((option) => option.value);
      if (needed) values.push(this.dataset.chooseSizeLabel);
      variantTitle.textContent = values.join(' / ');
    }
    this.#syncVisibility();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}

